import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { api } from '../../../services/api'
import type { RedmineProjectSuggestion, RedmineProjectSummary } from '../../../types/redmine'

/**
 * Searchable dropdown of Redmine projects, read live from the Redmine API.
 *
 * Two triggers share the same dropdown: a form field (opportunity modal) and
 * a chip (opportunity card). The list opens in a portal with fixed
 * positioning so neither the card's `overflow: hidden` nor its hover
 * transform can clip or shrink it, and it is sized to be readable
 * (min 480px wide, ~10 rows) regardless of how small the trigger is.
 *
 * For an existing opportunity the rows are ranked against it (code, client,
 * title) before the user types; typing narrows them. For a new opportunity
 * the plain project search is used.
 */

type Project = RedmineProjectSuggestion | RedmineProjectSummary

const FieldTrigger = styled.button<{ $empty?: boolean; $readonly?: boolean }>`
  width: 100%;
  min-height: 42px;
  padding: 0.55rem 2rem 0.55rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: white;
  text-align: left;
  cursor: ${({ $readonly }) => ($readonly ? 'default' : 'pointer')};
  color: ${({ $empty, theme }) => ($empty ? theme.colors.textLight : theme.colors.text)};
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20; }
  &::after {
    content: '▾';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const ChipTrigger = styled.button<{ $linked?: boolean; $readonly?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 14px;
  cursor: ${({ $readonly }) => ($readonly ? 'default' : 'pointer')};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid ${({ $linked }) => ($linked ? '#90CAF9' : '#BDBDBD')};
  background: ${({ $linked }) => ($linked ? '#E3F2FD' : '#FAFAFA')};
  color: ${({ $linked }) => ($linked ? '#1565C0' : '#616161')};
  &:hover { filter: brightness(0.96); }
`

const Dropdown = styled.div`
  position: fixed;
  z-index: 10000;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.md};
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 380px;
  overflow-y: auto;
`

const Item = styled.li<{ $disabled?: boolean; $active?: boolean }>`
  padding: 9px 10px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  &:hover { background: ${({ theme }) => theme.colors.secondary}; }
`

const Title = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const Reasons = styled.span`
  color: #2E7D32;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 8px;
`

const TextButton = styled.button<{ $danger?: boolean }>`
  background: none;
  border: none;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.danger : theme.colors.primary)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

export interface RedmineProjectSelectProps {
    /** Existing opportunity id → ranked suggestions and immediate linking. */
    opportunityId?: number | null
    /** Current identifier (what `redmine_codigo_imputacion` holds). */
    identifier?: string | null
    /** Display name of the linked project, when known. */
    projectName?: string | null
    /** Admins can change the link; everyone else gets a read-only trigger. */
    canEdit: boolean
    variant: 'field' | 'chip'
    /**
     * Called after a pick or a clear. For an existing opportunity the link
     * has already been stored in the backend by then; for a new one only the
     * identifier is reported and the link is resolved when the record exists.
     */
    onChange: (identifier: string, project: RedmineProjectSummary | null) => void
    placeholder?: string
}

const MIN_WIDTH = 480
const MARGIN = 8

export const RedmineProjectSelect = ({
    opportunityId,
    identifier,
    projectName,
    canEdit,
    variant,
    onChange,
    placeholder = 'Selecciona un proyecto de Redmine…',
}: RedmineProjectSelectProps) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [items, setItems] = useState<Project[] | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [active, setActive] = useState(0)
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const fetchItems = async (q: string) => {
        setLoading(true)
        setError(null)
        try {
            const res = opportunityId
                ? await api.redmine.suggestProjects(opportunityId, q, 15)
                : await api.redmine.searchProjects(q, 15)
            setItems(Array.isArray(res?.data) ? res.data : [])
            setActive(0)
        } catch (err: any) {
            const status = err?.response?.status
            setItems([])
            setError(
                status === 503
                    ? 'Redmine no está configurado: inicia sesión como administrador'
                    : err?.response?.data?.detail || 'Redmine no responde'
            )
        } finally {
            setLoading(false)
        }
    }

    const place = () => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (!rect) return
        const width = Math.min(Math.max(rect.width, MIN_WIDTH), window.innerWidth - 2 * MARGIN)
        const left = Math.min(Math.max(rect.left, MARGIN), window.innerWidth - width - MARGIN)
        const spaceBelow = window.innerHeight - rect.bottom
        const estimated = 520
        const top = spaceBelow < estimated && rect.top > estimated ? Math.max(MARGIN, rect.top - estimated) : rect.bottom + 4
        setPos({ top, left, width })
    }

    useLayoutEffect(() => {
        if (open) place()
    }, [open])

    useEffect(() => {
        if (!open) return
        fetchItems('')
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node
            if (dropdownRef.current?.contains(t) || triggerRef.current?.contains(t)) return
            setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
        const onMove = () => place()
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        window.addEventListener('resize', onMove)
        window.addEventListener('scroll', onMove, true)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
            window.removeEventListener('resize', onMove)
            window.removeEventListener('scroll', onMove, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, opportunityId])

    const onType = (q: string) => {
        setQuery(q)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => fetchItems(q.trim()), 250)
    }

    const isTaken = (p: Project) => !!p.linked_opportunity_id && p.linked_opportunity_id !== opportunityId

    const pick = async (p: Project) => {
        if (isTaken(p) || saving) return
        setSaving(true)
        setError(null)
        try {
            if (opportunityId) await api.redmine.linkOpportunity(opportunityId, { project_id: p.id })
            setOpen(false)
            setQuery('')
            onChange(p.identifier, p)
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'No se pudo enlazar el proyecto')
        } finally {
            setSaving(false)
        }
    }

    const clear = async () => {
        if (saving) return
        setSaving(true)
        try {
            if (opportunityId) await api.redmine.unlinkOpportunity(opportunityId)
            setOpen(false)
            setQuery('')
            onChange('', null)
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'No se pudo quitar el enlace')
        } finally {
            setSaving(false)
        }
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!items || items.length === 0) return
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)) }
        if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
        if (e.key === 'Enter') { e.preventDefault(); pick(items[active]) }
    }

    const label = identifier
        ? projectName && projectName !== identifier ? `${projectName}  (${identifier})` : identifier
        : ''

    const toggle = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (canEdit) setOpen((o) => !o)
    }

    const trigger = variant === 'field' ? (
        <FieldTrigger
            ref={triggerRef}
            type="button"
            $empty={!label}
            $readonly={!canEdit}
            onClick={toggle}
            title={label || placeholder}
        >
            {label ? `🔗 ${label}` : placeholder}
        </FieldTrigger>
    ) : (
        <ChipTrigger
            ref={triggerRef}
            type="button"
            $linked={!!identifier}
            $readonly={!canEdit}
            onClick={toggle}
            title={identifier ? `Proyecto Redmine: ${label}${canEdit ? ' (clic para cambiar)' : ''}` : 'Enlazar con un proyecto de Redmine'}
        >
            {identifier ? `🔗 ${projectName || identifier}` : canEdit ? '🔗 Asignar proyecto Redmine' : '🔗 Sin proyecto Redmine'}
        </ChipTrigger>
    )

    const dropdown = open && canEdit && pos && createPortal(
        <Dropdown
            ref={dropdownRef}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <SearchInput
                autoFocus
                placeholder="Buscar por nombre, código o cliente…"
                value={query}
                onChange={(e) => onType(e.target.value)}
                onKeyDown={onKeyDown}
            />
            {loading && <Muted>Consultando Redmine…</Muted>}
            {error && <Muted style={{ color: '#C62828' }}>{error}</Muted>}
            {!loading && items && (
                <List>
                    {items.length === 0 && (
                        <Item $disabled>
                            <Muted>{query ? 'Sin resultados en Redmine' : 'Sin sugerencias claras: escribe parte del nombre, del código o del cliente'}</Muted>
                        </Item>
                    )}
                    {items.map((p, idx) => {
                        const taken = isTaken(p)
                        const reasons = (p as RedmineProjectSuggestion).reasons || []
                        return (
                            <Item
                                key={p.id}
                                $disabled={taken || saving}
                                $active={idx === active}
                                onMouseEnter={() => setActive(idx)}
                                onClick={() => pick(p)}
                                title={taken ? `Ya enlazado a la oportunidad #${p.linked_opportunity_id}` : ''}
                            >
                                <span>
                                    <Title>{p.name}</Title>
                                    {p.status !== 1 && <Muted> · cerrado</Muted>}
                                    {taken && <Muted> · enlazado a #{p.linked_opportunity_id}</Muted>}
                                    {p.identifier === identifier && <Muted> · actual</Muted>}
                                </span>
                                <Muted>
                                    {p.identifier}
                                    {p.parent ? ` · en ${p.parent.name}` : ''}
                                </Muted>
                                {reasons.length > 0 && <Reasons>✓ {reasons.join(' · ')}</Reasons>}
                            </Item>
                        )
                    })}
                </List>
            )}
            <Footer>
                <Muted>{query ? 'Resultados de Redmine' : opportunityId ? 'Sugeridos para esta oportunidad' : 'Proyectos de Redmine'}</Muted>
                {identifier && <TextButton type="button" $danger onClick={clear} disabled={saving}>Quitar enlace</TextButton>}
            </Footer>
        </Dropdown>,
        document.body,
    )

    return (
        <>
            {trigger}
            {dropdown}
        </>
    )
}

export default RedmineProjectSelect
